<template>
    <div>
        <ui-table-card>
            <template #toolbar>
                <div>
                    <div class="ui-section-title">Documentos exigidos na inscrição</div>
                    <div class="ui-section-description">O que o proponente envia para se habilitar. Pessoa física e jurídica podem ter listas diferentes.</div>
                </div>
                <span class="ui-toolbar__spacer"></span>
                <v-btn v-if="$allowed('save')" color="primary" depressed @click="openEdit(null)"><v-icon left small>mdi-plus</v-icon>Adicionar exigência</v-btn>
            </template>
            <v-data-table :headers="headers" :items="items" :loading="loading" disable-sort disable-pagination hide-default-footer no-data-text="Nenhum documento exigido" loading-text="Carregando...">
                <template v-slot:[`item.title`]="{ item }">
                    <div class="ui-cell-main">{{ item.title }}</div>
                    <div v-if="item.description" class="ui-cell-sub">{{ item.description }}</div>
                </template>
                <template v-slot:[`item.applies_to_label`]="{ item }">{{ item.applies_to_label }}</template>
                <template v-slot:[`item.kind_label`]="{ item }"><ui-badge :text="item.kind_label" :tone="item.kind === 'deposit' ? 'warning' : 'neutral'" :dot="false" /></template>
                <template v-slot:[`item.required`]="{ item }">{{ item.required ? "Obrigatório" : "Opcional" }}</template>
                <template v-slot:[`item.actions`]="{ item }">
                    <ui-row-actions :actions="[
                        { text: 'Editar', icon: 'mdi-pencil-outline', handler: () => openEdit(item), disabled: !$allowed('save') },
                        { text: 'Excluir', icon: 'mdi-delete-outline', handler: () => remove(item), danger: true, disabled: !$allowed('save') },
                    ]" />
                </template>
            </v-data-table>
        </ui-table-card>

        <v-dialog v-model="edit.open" max-width="560" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header"><h3 class="ui-section-title">{{ edit.id ? "Editar exigência" : "Nova exigência" }}</h3></div>
                <div class="ui-card__body">
                    <v-text-field v-model="edit.form.title" label="Documento" outlined dense hide-details="auto" class="mb-4" maxlength="255" placeholder="Ex.: Contrato social e última alteração"></v-text-field>
                    <v-textarea v-model="edit.form.description" label="Orientação ao proponente" outlined dense rows="2" auto-grow hide-details="auto" class="mb-4"></v-textarea>
                    <v-row dense>
                        <v-col cols="6"><v-select v-model="edit.form.applies_to" :items="options.required_applies_to" label="Vale para" outlined dense hide-details="auto" :menu-props="{ offsetY: true }"></v-select></v-col>
                        <v-col cols="6"><v-select v-model="edit.form.kind" :items="options.required_kinds" label="Tipo" outlined dense hide-details="auto" :menu-props="{ offsetY: true }"></v-select></v-col>
                    </v-row>
                    <v-switch v-model="edit.form.required" inset dense hide-details label="Obrigatório para enviar a inscrição" class="mt-3"></v-switch>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="edit.saving" @click="edit.open = false">Cancelar</v-btn>
                    <v-btn color="primary" depressed :loading="edit.saving" :disabled="!String(edit.form.title || '').trim()" @click="save">Salvar</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import { API, notifyError, notifySuccess } from "@/components/competitive/competitive.util";

export default {
    name: "CompetitiveRequiredDocuments",
    props: {
        processId: { type: Number, required: true },
        options: { type: Object, required: true },
    },
    data: () => ({
        items: [],
        loading: false,
        edit: { open: false, id: null, saving: false, form: {} },
        headers: [
            { text: "Documento", value: "title" },
            { text: "Vale para", value: "applies_to_label", width: 160 },
            { text: "Tipo", value: "kind_label", width: 200 },
            { text: "Envio", value: "required", width: 120 },
            { text: "", value: "actions", align: "end", width: 48 },
        ],
    }),
    watch: {
        processId: {
            immediate: true,
            handler() {
                this.load();
            },
        },
    },
    methods: {
        async load() {
            this.loading = true;
            try {
                const result = await axios.get(`${API}/processes/${this.processId}/required-documents`);
                this.items = result.data.content;
                this.$emit("loaded", this.items);
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },
        openEdit(item) {
            this.edit = {
                open: true,
                id: item ? item.id : null,
                saving: false,
                form: item
                    ? { title: item.title, description: item.description || "", applies_to: item.applies_to, kind: item.kind, required: item.required }
                    : { title: "", description: "", applies_to: "both", kind: "document", required: true },
            };
        },
        async save() {
            this.edit.saving = true;
            try {
                if (this.edit.id) await axios.patch(`${API}/required-documents/${this.edit.id}`, this.edit.form);
                else await axios.post(`${API}/processes/${this.processId}/required-documents`, this.edit.form);
                notifySuccess(this, "Exigência salva.");
                this.edit.open = false;
                await this.load();
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.edit.saving = false;
            }
        },
        async remove(item) {
            const confirmed = await this.$dialog.confirm({
                title: "Excluir exigência",
                text: `"${item.title}" deixa de ser pedido nas inscrições. Arquivos já enviados continuam nas inscrições como documentos adicionais. Confirma?`,
                actions: { false: "Cancelar", true: { text: "Excluir", color: "error" } },
            });
            if (!confirmed) return;
            try {
                await axios.delete(`${API}/required-documents/${item.id}`);
                await this.load();
            } catch (error) {
                notifyError(this, error);
            }
        },
    },
};
</script>
